/**
 * useReversedAnchor
 *
 * Encapsulates the chat-style ("reversed") mode bookkeeping for `VirtualList`:
 *
 *   1. First-mount **bottom anchor** + settle loop — snap the scroll to the
 *      bottom of the list on first non-empty mount, then re-snap as the top
 *      spacer grows under late-arriving measurements.
 *   2. **Sticky-on-append / preserve-on-prepend** — on subsequent items
 *      changes, distinguish appends (growth at the bottom) from prepends
 *      (growth at the top) using a captured first-item key, and adjust
 *      `scrollTop` accordingly.
 *   3. **Bottom-pin padding** — when natural content height < viewport
 *      height, apply a `paddingTop` to the inner container so the items
 *      visually pin to the BOTTOM of the viewport (chat semantics).
 *   4. **`onReachedEnd` gate** — expose `hasInitialBottomAnchorCompleted` so
 *      the top sentinel's IntersectionObserver doesn't fire during the
 *      mount-time anchor snap window.
 *   5. **Pre-update scroll metrics ref** — owned here, refreshed by the
 *      parent's scroll listener via the returned `refreshLatestScrollMetricsRef`.
 *
 * Why a sibling hook (and not inline in `VirtualList.tsx`):
 *   - The reversed-mode subsystem is a cohesive concept (one of the modes
 *     `VirtualList` operates in) but its bookkeeping spans 5+ refs and 2
 *     effects with non-obvious cross-dependencies — splitting it out keeps
 *     the default-mode reader from wading through reversed-mode-only state.
 *   - File-private to the `virtualList/` folder; no new public export from
 *     `VirtualList.tsx` or `index.ts`.
 *
 * Behavioral notes (carried verbatim from the prior inline implementation):
 *   - On reversed=false this hook is a near-no-op: it returns zeroed values
 *     and skips subscribing observers.
 *   - The pre-update scroll-metrics ref is owned here BUT refreshed by the
 *     parent's scroll listener (which lives in `VirtualList.tsx` because it
 *     also drives `updateVisibleRange` for the non-reversed path). The
 *     parent calls `refreshLatestScrollMetricsRef.current?.()` from its
 *     scroll handler; we re-assign the ref's closure on every render so the
 *     parent always invokes a fresh capture over `isReversed`/scroll
 *     accessors. The ref itself is stable across renders, so the parent's
 *     scroll listener does NOT re-subscribe when this closure changes —
 *     preserving the load-bearing "no re-subscribe on items change"
 *     invariant documented at `VirtualList.tsx`'s scroll-listener effect.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject, RefObject } from 'react';

/**
 * Tolerance (in pixels) used by `reversed` mode to decide whether the user
 * is currently pinned to the bottom of the list. Sub-pixel rounding (CSS
 * zoom, fractional `clientHeight`, devicePixelRatio quirks) can leave a few
 * pixels between `scrollTop + clientHeight` and `scrollHeight` even when the
 * user is visually at the bottom — this fudge factor absorbs that.
 */
export const STICKY_BOTTOM_TOLERANCE_PX = 8;

/**
 * Tolerance (in pixels) used by `reversed` mode when deciding whether the
 * computed bottom-pin padding has changed enough to warrant a re-render.
 * `getBoundingClientRect().height` is fractional while `clientHeight` is an
 * integer; without an epsilon a raw `!==` check fires on every observer
 * callback (sub-pixel drift caused by devicePixelRatio, fractional layout,
 * or CSS zoom) and produces an infinite re-render / scrollbar-flicker loop.
 * 0.5px is large enough to absorb that jitter and small enough that any real
 * layout change still triggers a recompute.
 */
const PADDING_EPSILON_PX = 0.5;

interface ScrollMetricsSnapshot {
    scrollTop: number;
    scrollHeight: number;
    viewportHeight: number;
    wasAtBottom: boolean;
}

export interface UseReversedAnchorParams<T> {
    /** True when `VirtualList` is in reversed mode. When false the hook is a near-no-op. */
    isReversed: boolean;
    /** Live items array (oldest at index 0). */
    items: T[];
    /** Stable per-item key extractor (mirrors `VirtualListProps.getItemKey`). */
    getItemKey: (item: T, index: number) => string | number;
    /** Ref to the inner container `<div>` whose height we measure for bottom-pin math. */
    containerRef: RefObject<HTMLDivElement | null>;
    /** Stable accessor for the scroll target (`HTMLElement` or `Window`). */
    getScrollElement: () => HTMLElement | Window;
    /** Stable `scrollTop` accessor. */
    getScrollTop: (el: HTMLElement | Window) => number;
    /** Stable viewport-height accessor (raw — caller does NOT subtract padding). */
    getViewportHeight: (el: HTMLElement | Window) => number;
    /**
     * Ref to the parent's latest `updateVisibleRange` closure. Invoked via
     * `.current?.()` so the hook does NOT pull `updateVisibleRange` into its
     * own dep arrays — preserving the parent's "no re-subscribe on items
     * change" invariant.
     */
    updateVisibleRangeRef: MutableRefObject<(() => void) | null>;
    /**
     * Px of sticky/fixed chrome overlapping the TOP of the *window* viewport
     * (e.g. an app bar). Subtracted from `window.innerHeight` when computing
     * the short-list bottom-pin padding. No-op unless the resolved scroll
     * element is `window`. Default 0.
     */
    bottomPinInsetTop: number;
    /**
     * Px of sticky/fixed chrome overlapping the BOTTOM of the *window*
     * viewport (e.g. a chat composer). Subtracted from `window.innerHeight`
     * when computing the short-list bottom-pin padding. No-op unless the
     * resolved scroll element is `window`. Default 0.
     */
    bottomPinInsetBottom: number;
}

export interface UseReversedAnchorReturn {
    /** Padding-top applied to the inner container to bottom-pin short lists. */
    bottomPinPaddingTop: number;
    /** Gate for the `onReachedEnd` IntersectionObserver (true once mount-snap settled). */
    hasInitialBottomAnchorCompleted: boolean;
    /**
     * Ref whose `.current` is the latest pre-update scroll-metrics refresher.
     * Parent's scroll listener calls `refreshLatestScrollMetricsRef.current?.()`
     * on every scroll event. The ref itself is stable across renders so the
     * scroll listener does NOT re-subscribe when the closure changes.
     */
    refreshLatestScrollMetricsRef: MutableRefObject<(() => void) | null>;
}

export function useReversedAnchor<T>(
    params: UseReversedAnchorParams<T>,
): UseReversedAnchorReturn {
    const {
        isReversed,
        items,
        getItemKey,
        containerRef,
        getScrollElement,
        getScrollTop,
        getViewportHeight,
        updateVisibleRangeRef,
        bottomPinInsetTop,
        bottomPinInsetBottom,
    } = params;

    // Tracks whether we have already snapped to the bottom on first non-empty
    // mount. Mirrors the role of `hasScrolledToInitialIndex` for the
    // top-anchored case so the bottom-anchor runs exactly once.
    const hasAnchoredToBottomRef = useRef(false);

    // Gates the `onReachedEnd` IntersectionObserver so the top sentinel cannot
    // intersect-and-fire during the two-RAF window between mount and the
    // bottom-anchor snap completing. Without this gate the sentinel sits at
    // `scrollTop = 0` (still within the `rootMargin` extension) at
    // observer-creation time, so the observer would fire `onReachedEnd`
    // immediately on mount before the user has interacted. For non-reversed
    // mode the gate is permanently open (`true`) so the observer behaves
    // exactly as before. Flipped to `true` from inside the settle loop, once
    // the viewport is at the bottom and the sentinel is genuinely off-screen.
    const [hasInitialBottomAnchorCompleted, setHasInitialBottomAnchorCompleted] =
        useState(!isReversed);

    // Latest scroll metrics, continuously refreshed by the parent's scroll
    // handler (and seeded ONCE at scroll-listener subscribe time). Because the
    // browser does not fire a `scroll` event during React's render/commit
    // phase, this ref still reflects the LAST user-observed scroll state at
    // the moment the items-change effect below runs — i.e. pre-update
    // metrics, which is exactly what we need to decide between
    // sticky-on-append and preserve-on-prepend.
    //
    // CRITICAL: the parent's scroll-listener effect MUST NOT re-subscribe on
    // items change, otherwise its mount-time seed would re-run AFTER React
    // has already committed the new items to the DOM and would clobber the
    // pre-update snapshot with post-commit metrics — defeating the entire
    // purpose of this ref. We achieve "no re-subscribe on items change" by
    // routing the refresher through `refreshLatestScrollMetricsRef` (stable
    // identity) so the parent's scroll listener never depends on the
    // closure's identity. See the CRITICAL block in the parent's scroll
    // listener for the matching half of this invariant.
    const latestScrollMetricsRef = useRef<ScrollMetricsSnapshot | null>(null);

    // The first-item key captured at the end of the previous render's
    // items-change effect. Used in the next render to discriminate append
    // (first key unchanged → growth at the bottom) from prepend (first key
    // changed → growth at the top). Refreshed at the END of each items-change
    // effect so the NEXT items change can compare against it.
    const previousFirstItemKeyRef = useRef<string | number | null>(null);

    // When the rendered content's natural height is less than the scroll
    // viewport's `clientHeight`, this padding is applied to the TOP of the
    // container so the items visually pin to the BOTTOM of the viewport
    // (chat semantics — newest at the bottom, growing upward). When content
    // grows past the viewport, the padding goes to 0 and the normal
    // `scrollTop`-driven anchor math (sticky-on-append, preserve-on-prepend,
    // bottom-anchor on mount) takes over without interference.
    //
    // Strategy chosen over a caller-side `justifyContent: flex-end` on the
    // outer scroll wrapper because:
    //   1. It keeps `VirtualList(reversed)` self-contained — callers don't
    //      need to opt-in via fragile CSS that, if forgotten, silently
    //      regresses bottom-pinning.
    //   2. The existing scroll-anchor math (`getMaxScrollTop()` etc.) keeps
    //      working: padding contributes to `scrollEl.scrollHeight`, so when
    //      `naturalContentHeight < viewportHeight` we have
    //      `scrollHeight === clientHeight`, `getMaxScrollTop() === 0`,
    //      `setScrollTop(0)` is a no-op, and the content is visually at the
    //      bottom because of the padding alone.
    //
    // We track BOTH a state (drives rendering) and a ref (read by the
    // recomputation so it knows how much padding to subtract from the
    // currently-measured container height to recover the natural content
    // height). The convergence is tight (one feedback ResizeObserver fire
    // after a padding change confirms `desired === current` and the loop
    // stops); no infinite recursion.
    const [bottomPinPaddingTop, setBottomPinPaddingTop] = useState(0);
    const bottomPinPaddingTopRef = useRef(0);

    // Tracks the `items.length` value the bottom-pin effect last subscribed
    // against. Used to detect the empty→non-empty boundary so we can schedule
    // a single `requestAnimationFrame` recompute against the freshly-mounted
    // DOM — the synchronous `recomputeBottomPinPadding()` call inside the
    // effect runs BEFORE the items commit (the effect fires on the same tick
    // the new render is queued), so without the RAF the first measurement
    // sees the still-empty container and lands at the wrong padding until
    // the next ResizeObserver fire.
    const previousBottomPinItemsLengthRef = useRef(0);

    // Stable ref to the latest pre-update scroll-metrics refresher. Parent's
    // scroll listener invokes `.current?.()` on every scroll event. We
    // re-assign `.current` on every render so the parent always invokes the
    // freshest closure — the ref's IDENTITY stays stable, so the parent's
    // scroll listener never re-subscribes on this changing.
    const refreshLatestScrollMetricsRef = useRef<(() => void) | null>(null);
    refreshLatestScrollMetricsRef.current = () => {
        if (!isReversed) return;
        const scrollEl = getScrollElement();
        const scrollTop = getScrollTop(scrollEl);
        const viewportHeight = getViewportHeight(scrollEl);
        const scrollHeight = scrollEl instanceof Window
            ? document.documentElement.scrollHeight
            : (scrollEl as HTMLElement).scrollHeight;
        const wasAtBottom =
            (scrollHeight - scrollTop - viewportHeight) <= STICKY_BOTTOM_TOLERANCE_PX;
        latestScrollMetricsRef.current = { scrollTop, scrollHeight, viewportHeight, wasAtBottom };
    };

    // Recompute `bottomPinPaddingTop` so items pin to the BOTTOM of the
    // viewport when natural content height < clientHeight.
    //
    // The natural content height is recovered as
    //   container.boundingRect.height - currentlyAppliedPadding
    // because the padding itself is part of the rendered container. The
    // "currently applied padding" is read DIRECTLY from the DOM via
    // `getComputedStyle(containerRef.current).paddingTop` so it stays in
    // lockstep with the `measuredContentHeight` we just read from the same
    // element in the same frame — see the CRITICAL comment inside the body
    // for why reading the ref here would race with React's commit cycle and
    // feed a multi-frame oscillation.
    //
    // Convergence: a padding change resizes the container, which fires the
    // observer once more; the second pass sees the new measured height equal
    // to (naturalContent + newPadding), reads the matching `appliedPaddingTop`
    // from the DOM, recomputes `desired === newPadding`, and the loop
    // terminates.
    //
    // Sub-pixel stability: `getBoundingClientRect().height` is FRACTIONAL while
    // `clientHeight` is an integer. Without rounding + an epsilon, devicePixelRatio
    // and fractional layout produced a microscopic drift each observer fire that
    // never satisfied a raw `!==` check — the loop would re-render forever and the
    // browser would render the resulting unstable container height as a flickering
    // scrollbar in the parent scroll region. `Math.round` on `desired` keeps the
    // applied padding pixel-stable; the epsilon absorbs any remaining fractional
    // jitter on the measurement side.
    const recomputeBottomPinPadding = useCallback(() => {
        if (!isReversed || !containerRef.current) {
            return;
        }

        const scrollEl = getScrollElement();
        const rawViewportHeight = getViewportHeight(scrollEl);
        // Read the scroll element's CONTENT-BOX height (i.e. subtract its own
        // `paddingTop`/`paddingBottom`). `clientHeight` includes vertical
        // padding, so using it as the cap would over-fill by exactly the
        // parent's vertical padding — the bottom-pinned container would end
        // up taller than the visible area, pushing items past the visible
        // bottom edge AND producing a spurious scrollbar.
        //
        // For `Window` scroll targets there is no analogous parent padding to
        // strip (`window.innerHeight` is already the visible viewport).
        // Instead, subtract the caller-supplied viewport inset: sticky/fixed
        // chrome (an app bar above, a composer below) that overlaps the window
        // viewport in document flow. `window.innerHeight` counts that chrome,
        // so without the inset a short list would pad to fill the WHOLE
        // viewport — leaving blank scrollable space and offsetting the newest
        // bubble from a sticky footer. The inset defaults to 0 for callers
        // that don't pass it (`reversedBottomPinInset`), so this is a no-op
        // for every existing window-scroll reversed caller. Clamp `>= 0` so a
        // momentarily over-large inset (mid-measurement) can't produce a
        // negative cap.
        let viewportHeight = rawViewportHeight;
        if (scrollEl instanceof HTMLElement) {
            const cs = window.getComputedStyle(scrollEl);
            const padTop = parseFloat(cs.paddingTop) || 0;
            const padBottom = parseFloat(cs.paddingBottom) || 0;
            viewportHeight = rawViewportHeight - padTop - padBottom;
        } else {
            viewportHeight = Math.max(
                0,
                rawViewportHeight - bottomPinInsetTop - bottomPinInsetBottom,
            );
        }
        // Bail early when measurements are not yet meaningful — running with a
        // zero viewport produces a spurious `desired = 0` that races with the
        // first real measurement, and running on a non-empty list with a zero
        // measured content height produces a spurious `desired = viewportHeight`
        // (flash-to-bottom then jump-to-top once items commit).
        if (viewportHeight <= 0) {
            return;
        }

        const measuredContentHeight = containerRef.current.getBoundingClientRect().height;
        if (measuredContentHeight <= 0 && items.length > 0) {
            return;
        }

        // CRITICAL: derive `appliedPaddingTop` from the DOM (not from
        // `bottomPinPaddingTopRef.current`) because this callback is invoked
        // synchronously from ResizeObservers, which fire AFTER layout but
        // BEFORE React's next commit. At that moment the DOM still reflects
        // the PREVIOUS render's `paddingTop`, while `bottomPinPaddingTopRef`
        // already holds the value we just decided to commit. Subtracting the
        // ref from `measuredContentHeight` would produce an internally
        // inconsistent `naturalContentHeight` (negative when padding grew,
        // double-counted when padding shrank) and feeds a multi-frame
        // oscillation between two padding states. Reading `paddingTop`
        // straight off the live `containerRef.current` keeps both halves of
        // the subtraction in the SAME frame, so the recompute converges in
        // one ResizeObserver fire instead of looping forever.
        const containerStyles = window.getComputedStyle(containerRef.current);
        const appliedPaddingTop = parseFloat(containerStyles.paddingTop) || 0;
        const naturalContentHeight = measuredContentHeight - appliedPaddingTop;
        const rawDesired = naturalContentHeight < viewportHeight
            ? Math.max(0, viewportHeight - naturalContentHeight)
            : 0;
        const desired = Math.round(rawDesired);
        const delta = Math.abs(desired - bottomPinPaddingTopRef.current);
        const willCommit = delta > PADDING_EPSILON_PX;

        if (willCommit) {
            bottomPinPaddingTopRef.current = desired;
            setBottomPinPaddingTop(desired);
        }
    }, [isReversed, containerRef, getScrollElement, getViewportHeight, items.length, bottomPinInsetTop, bottomPinInsetBottom]);

    // Bottom-anchor on first non-empty mount, plus scroll-anchor preservation
    // across content growth.
    //
    // - First non-empty mount → snap scroll to `scrollHeight - clientHeight`
    //   after two RAFs (first frame mounts DOM, second frame layout settles).
    // - On items growth, we read pre-update state from two refs that are
    //   refreshed OUTSIDE this effect so they still reflect the previous
    //   render's state when this effect runs:
    //     • `latestScrollMetricsRef` — kept fresh by the parent's scroll
    //       handler (and seeded at subscribe time). Since no scroll event
    //       fires during React's render/commit phase, this ref holds the
    //       user's last-observed scroll state at the moment this effect runs.
    //     • `previousFirstItemKeyRef` — refreshed at the END of the previous
    //       render's run of this effect, so it holds the first-item key from
    //       BEFORE the current items prop landed.
    //   We compare the previous first-item key to the new one:
    //     • Keys match → growth happened at the bottom (append):
    //         - user was at the bottom → re-snap to the new bottom
    //           (sticky-on-append).
    //         - user was NOT at the bottom → leave `scrollTop` alone so the
    //           user is not yanked away from the older content they were
    //           reading.
    //     • Keys differ → growth happened at the top (prepend):
    //         - set `scrollTop = oldScrollTop + delta` so the viewport stays
    //           pinned to the same logical content (preserve-on-prepend).
    //
    // At the end we refresh `previousFirstItemKeyRef` with the new first-item
    // key, and refresh `latestScrollMetricsRef` to reflect the post-update
    // DOM state — both so the NEXT items change has a correct baseline.
    useEffect(() => {
        if (!isReversed) return;

        if (items.length === 0) {
            hasAnchoredToBottomRef.current = false;
            previousFirstItemKeyRef.current = null;
            // Re-gate the onReachedEnd observer so a subsequent non-empty
            // mount goes through the bottom-anchor snap again before the
            // top sentinel is allowed to fire.
            setHasInitialBottomAnchorCompleted(false);
            return;
        }
        if (!containerRef.current) return;

        const scrollEl = getScrollElement();
        const getMaxScrollTop = (): number => {
            if (scrollEl instanceof Window) {
                return document.documentElement.scrollHeight - window.innerHeight;
            }
            const el = scrollEl as HTMLElement;
            return el.scrollHeight - el.clientHeight;
        };
        const setScrollTop = (top: number): void => {
            if (scrollEl instanceof Window) {
                window.scrollTo({ top, behavior: 'auto' });
            } else {
                (scrollEl as HTMLElement).scrollTop = top;
            }
        };
        const readCurrentScrollHeight = (): number =>
            scrollEl instanceof Window
                ? document.documentElement.scrollHeight
                : (scrollEl as HTMLElement).scrollHeight;

        const newFirstItemKey = getItemKey(items[0], 0);

        // First non-empty mount → bottom-anchor after two RAFs so layout
        // settles, then re-snap in a small settle loop until `scrollHeight`
        // stops growing.
        //
        // Why the settle loop is needed: the top spacer's height (see the
        // `topSpacerHeight` memo in `VirtualList.tsx`) is summed from each
        // off-screen item's `getItemHeight(key)`, which returns
        // `defaultItemHeight` for any item that has not yet been measured by
        // the `measurementObserver` ResizeObserver. When `defaultItemHeight`
        // is smaller than the real measured row height (e.g. callers passing
        // a 48px hint for chat-style usage where bubbles actually measure
        // 150–200px), the top spacer is under-estimated → `scrollHeight` is
        // under-estimated → `getMaxScrollTop()` returns a value below the
        // true bottom on the first snap. Once the ResizeObserver fires with
        // real heights, the spacer grows, `scrollHeight` grows, but
        // `scrollTop` was already set to the now-stale lower value, leaving
        // the list visually scrolled above the true bottom.
        //
        // The fix: after the two-RAF initial snap, keep re-snapping to the
        // (now larger) `getMaxScrollTop()` for up to `MAX_SETTLE_PASSES`
        // additional RAFs.
        //
        // Loop invariants — both load-bearing:
        //
        //   (1) Pass 0 ALWAYS re-snaps unconditionally. The ResizeObserver →
        //       `updateVisibleRange` → React commit → spacer-memo chain that
        //       grows `scrollHeight` is asynchronous and multi-step; in a
        //       typical browser run NONE of it has finished by the time the
        //       first `settlePass` reads `currentScrollHeight`. Treating
        //       "scrollHeight unchanged" as "done" on pass 0 would exit
        //       before the spacer growth has even had a chance to land —
        //       defeating the entire purpose of the settle loop. We only
        //       allow the "scrollHeight unchanged" exit AFTER at least one
        //       re-snap has been performed (`pass >= 1`).
        //
        //   (2) User-scroll detection compares `currentScrollTop` against
        //       the PREVIOUS snap target (`lastSnapTarget`) — NOT against a
        //       freshly-computed `scrollHeight - scrollTop - viewport`
        //       delta. The reason: between snaps, the spacer can grow by
        //       more than `STICKY_BOTTOM_TOLERANCE_PX` (the very situation
        //       we're recovering from), and that growth would otherwise
        //       look identical to "the user scrolled away from the bottom"
        //       under the delta formula — causing the loop to bail
        //       precisely when it has the most work to do. Anchoring the
        //       check to the last programmatic snap target makes spacer
        //       growth invisible to it: only an ACTUAL user scroll between
        //       passes moves `scrollTop` away from `lastSnapTarget`.
        //
        // The `onReachedEnd` sentinel gate (`hasInitialBottomAnchorCompleted`)
        // only opens AFTER the settle loop finishes — otherwise the top
        // sentinel could intersect-and-fire while `scrollHeight` is still
        // growing during settle, before the viewport has reached the true
        // bottom.
        if (!hasAnchoredToBottomRef.current) {
            hasAnchoredToBottomRef.current = true;
            previousFirstItemKeyRef.current = newFirstItemKey;
            const MAX_SETTLE_PASSES = 5;
            let raf2 = 0;
            let settleRaf = 0;
            const raf1 = requestAnimationFrame(() => {
                raf2 = requestAnimationFrame(() => {
                    let lastSnapTarget = getMaxScrollTop();
                    setScrollTop(lastSnapTarget);
                    let previousScrollHeight = readCurrentScrollHeight();
                    let pass = 0;
                    const settlePass = () => {
                        const currentScrollTop = getScrollTop(scrollEl);
                        const currentViewportHeight = getViewportHeight(scrollEl);

                        // Invariant (2): user-scroll detection against the
                        // previous snap target, NOT a scrollHeight-derived
                        // delta. If the user actually scrolled between the
                        // previous re-snap and now, `scrollTop` will differ
                        // from `lastSnapTarget` by more than the tolerance.
                        // Pure spacer growth does NOT move `scrollTop`, so
                        // this check is immune to the measurement-driven
                        // growth that the loop is recovering from.
                        const userScrolled =
                            Math.abs(currentScrollTop - lastSnapTarget) > STICKY_BOTTOM_TOLERANCE_PX;

                        if (userScrolled) {
                            // Bail without re-snapping — the user took over.
                            const currentScrollHeight = readCurrentScrollHeight();
                            latestScrollMetricsRef.current = {
                                scrollTop: currentScrollTop,
                                scrollHeight: currentScrollHeight,
                                viewportHeight: currentViewportHeight,
                                wasAtBottom:
                                    (currentScrollHeight - currentScrollTop - currentViewportHeight)
                                    <= STICKY_BOTTOM_TOLERANCE_PX,
                            };
                            setHasInitialBottomAnchorCompleted(true);
                            requestAnimationFrame(() => updateVisibleRangeRef.current?.());
                            return;
                        }

                        // Always re-snap first — even on pass 0, where the
                        // RO → commit → spacer-growth chain may not have
                        // landed yet. The re-snap is a no-op when the
                        // bottom hasn't moved (target unchanged) and is
                        // exactly the work we need to do when it has.
                        lastSnapTarget = getMaxScrollTop();
                        setScrollTop(lastSnapTarget);

                        const currentScrollHeight = readCurrentScrollHeight();

                        // Invariant (1): only allow the "scrollHeight
                        // unchanged" exit AFTER pass 0's mandatory re-snap.
                        // On pass 0, even if `scrollHeight` matches the
                        // pre-RAF baseline, we still schedule one more pass
                        // so the async measurement chain has a chance to
                        // commit before we declare done.
                        const heightStable = currentScrollHeight === previousScrollHeight;
                        const canExitOnStable = pass >= 1;

                        if ((heightStable && canExitOnStable) || pass >= MAX_SETTLE_PASSES) {
                            // Settle done. Refresh the latest-metrics ref
                            // to reflect the final post-snap baseline so
                            // the very next items-change has a fresh
                            // `wasAtBottom` and `scrollHeight` to read.
                            // After the re-snap, `wasAtBottom` is true by
                            // construction (we just snapped to the bottom).
                            latestScrollMetricsRef.current = {
                                scrollTop: lastSnapTarget,
                                scrollHeight: currentScrollHeight,
                                viewportHeight: currentViewportHeight,
                                wasAtBottom: true,
                            };
                            // Open the onReachedEnd gate: the viewport is
                            // now snapped to the bottom, so the top sentinel
                            // is genuinely off-screen and any subsequent
                            // intersection reflects a real user scroll toward
                            // the load-more edge.
                            setHasInitialBottomAnchorCompleted(true);
                            requestAnimationFrame(() => updateVisibleRangeRef.current?.());
                            return;
                        }

                        // Either scrollHeight grew (top spacer expanded as
                        // measurements landed) or we're still on pass 0 →
                        // schedule another pass.
                        previousScrollHeight = currentScrollHeight;
                        pass += 1;
                        settleRaf = requestAnimationFrame(settlePass);
                    };
                    settleRaf = requestAnimationFrame(settlePass);
                });
            });
            return () => {
                if (settleRaf) cancelAnimationFrame(settleRaf);
                if (raf2) cancelAnimationFrame(raf2);
                cancelAnimationFrame(raf1);
            };
        }

        // Subsequent updates: read the pre-update metrics and previous first
        // key. These refs were populated BEFORE the current items prop
        // landed: the parent's scroll listener keeps `latestScrollMetricsRef`
        // fresh on every user scroll (and there is no scroll event during
        // the React commit), and `previousFirstItemKeyRef` was set at the
        // end of the previous render's run of this effect.
        const pre = latestScrollMetricsRef.current;
        const previousFirstItemKey = previousFirstItemKeyRef.current;

        // Always update the previous-key baseline for the next round.
        previousFirstItemKeyRef.current = newFirstItemKey;

        if (!pre) {
            // No baseline yet (e.g. scroll listener disabled because the list
            // was inactive). Refresh metrics from the current DOM and bail —
            // we can't safely compute a delta without a pre-update reference.
            const scrollTop = getScrollTop(scrollEl);
            const viewportHeight = getViewportHeight(scrollEl);
            const scrollHeight = readCurrentScrollHeight();
            latestScrollMetricsRef.current = {
                scrollTop,
                scrollHeight,
                viewportHeight,
                wasAtBottom: (scrollHeight - scrollTop - viewportHeight) <= STICKY_BOTTOM_TOLERANCE_PX,
            };
            return;
        }

        const newScrollHeight = readCurrentScrollHeight();
        const grew = newScrollHeight > pre.scrollHeight;

        // Helper to refresh latestScrollMetricsRef from the post-update DOM
        // once we're done handling this update.
        const refreshLatestMetricsToCurrentDom = () => {
            const scrollTop = getScrollTop(scrollEl);
            const viewportHeight = getViewportHeight(scrollEl);
            const scrollHeight = readCurrentScrollHeight();
            latestScrollMetricsRef.current = {
                scrollTop,
                scrollHeight,
                viewportHeight,
                wasAtBottom: (scrollHeight - scrollTop - viewportHeight) <= STICKY_BOTTOM_TOLERANCE_PX,
            };
        };

        if (!grew) {
            refreshLatestMetricsToCurrentDom();
            return;
        }

        // Distinguish append (growth at the bottom) from prepend (growth at
        // the top) by comparing the previous first-item key to the new one.
        // If the first key is unchanged, the list grew at the end; if it
        // changed, older items were inserted at the front.
        const isPrepend = previousFirstItemKey !== null && newFirstItemKey !== previousFirstItemKey;

        if (isPrepend) {
            // Preserve-on-prepend: keep the viewport pinned to the same
            // logical content by shifting scrollTop by the growth delta.
            const delta = newScrollHeight - pre.scrollHeight;
            setScrollTop(pre.scrollTop + delta);
            refreshLatestMetricsToCurrentDom();
            return;
        }

        // Append case (growth at the bottom).
        if (pre.wasAtBottom) {
            // Sticky-on-append: re-snap to the new bottom in a small settle
            // loop so late-arriving item measurements (a freshly-appended
            // bubble whose real measured height exceeds `defaultItemHeight`)
            // are accounted for before we declare done. Mirrors the
            // mount-time settle loop above — same `MAX_SETTLE_PASSES` and
            // same exit condition (scrollHeight stable from the previous
            // pass). Without the settle loop, a single RAF re-snap reads a
            // `scrollHeight` that still budgets only `defaultItemHeight` for
            // the new item, lands short of the true bottom, and leaves the
            // newest bubble partially clipped below the viewport.
            const MAX_SETTLE_PASSES = 5;
            let pass = 0;
            let lastSnapTarget = getMaxScrollTop();
            setScrollTop(lastSnapTarget);
            let previousScrollHeight = readCurrentScrollHeight();
            let settleRaf = 0;
            const settlePass = () => {
                const newTarget = getMaxScrollTop();
                if (newTarget !== lastSnapTarget) {
                    lastSnapTarget = newTarget;
                    setScrollTop(lastSnapTarget);
                }
                const newScrollHeight = readCurrentScrollHeight();
                const stable = newScrollHeight === previousScrollHeight;
                previousScrollHeight = newScrollHeight;
                pass += 1;
                if (!stable && pass < MAX_SETTLE_PASSES) {
                    settleRaf = requestAnimationFrame(settlePass);
                    return;
                }
                refreshLatestMetricsToCurrentDom();
            };
            const raf = requestAnimationFrame(settlePass);
            return () => {
                if (settleRaf) cancelAnimationFrame(settleRaf);
                cancelAnimationFrame(raf);
            };
        }

        // User had scrolled up to read older content. Leave the viewport
        // exactly where it is — do NOT auto-scroll, do NOT apply the delta.
        // `scrollTop` already excludes the newly appended bottom content, so
        // no adjustment is needed to keep the user pinned to what they were
        // reading.
        refreshLatestMetricsToCurrentDom();
    }, [items, isReversed, containerRef, getScrollElement, getScrollTop, getViewportHeight, getItemKey, updateVisibleRangeRef]);

    // Drive `bottomPinPaddingTop` from a single source of truth.
    //
    // This effect REPLACES two earlier separate effects (a "trigger" effect
    // that subscribed observers and a "reset" effect that zeroed the padding
    // when the list went empty). Splitting the two created a flip-flop:
    // whichever effect ran last won the committed state, and the next observer
    // fire flipped it again — a never-settling loop that drove the
    // empty-list-scrollbar-flicker bug.
    //
    // Branches:
    //   - reversed=false OR items.length===0 → zero the padding and skip the
    //     observers entirely (no content to measure, nothing to pin).
    //   - reversed=true with items → seed once synchronously (so the first
    //     paint is correct), then keep the padding in sync via the same
    //     ResizeObserver set as before.
    //
    // Window scroll target: when the scroll element is `window`, viewport
    // height comes from `window.innerHeight`; we listen to the window's
    // `resize` event instead of a ResizeObserver (which can't observe Window).
    useEffect(() => {
        if (!isReversed || items.length === 0) {
            if (bottomPinPaddingTopRef.current !== 0) {
                bottomPinPaddingTopRef.current = 0;
                setBottomPinPaddingTop(0);
            }
            previousBottomPinItemsLengthRef.current = items.length;
            return;
        }

        recomputeBottomPinPadding();

        // Crossing the empty→non-empty boundary: the synchronous recompute
        // above runs before the newly-rendered items have committed to the
        // DOM, so the container's measured height is still that of the
        // empty baseline. Schedule a single RAF recompute so the next
        // measurement reads the freshly-mounted bubbles and lands at the
        // correct padding on the first paint — without this, items briefly
        // sit at the top until a later ResizeObserver fire corrects them.
        const crossedEmptyBoundary = previousBottomPinItemsLengthRef.current === 0;
        previousBottomPinItemsLengthRef.current = items.length;
        let rafId: number | null = null;
        if (crossedEmptyBoundary) {
            rafId = requestAnimationFrame(() => {
                recomputeBottomPinPadding();
            });
        }

        const observers: Array<{ disconnect: () => void }> = [];

        if (containerRef.current) {
            const containerObserver = new ResizeObserver(() => {
                recomputeBottomPinPadding();
            });
            containerObserver.observe(containerRef.current);
            observers.push(containerObserver);
        }

        const scrollEl = getScrollElement();
        let removeScrollListener: (() => void) | null = null;
        if (scrollEl instanceof Window) {
            const onResize = () => {
                recomputeBottomPinPadding();
            };
            window.addEventListener('resize', onResize);
            removeScrollListener = () => window.removeEventListener('resize', onResize);
        } else {
            const scrollObserver = new ResizeObserver(() => {
                recomputeBottomPinPadding();
            });
            scrollObserver.observe(scrollEl as HTMLElement);
            observers.push(scrollObserver);
        }

        return () => {
            if (rafId !== null) cancelAnimationFrame(rafId);
            observers.forEach((o) => o.disconnect());
            removeScrollListener?.();
        };
    }, [isReversed, items.length, containerRef, getScrollElement, recomputeBottomPinPadding]);

    return {
        bottomPinPaddingTop,
        hasInitialBottomAnchorCompleted,
        refreshLatestScrollMetricsRef,
    };
}
